/**
 * AppBouncer SDK Browser Bundle Entry Point
 *
 * This file is the entry point for the browser bundle built with esbuild.
 * It exports all SDK modules and auto-initializes the VerificationController.
 */

// Export all SDK modules for programmatic access
export * from './index';

// Import controller for auto-initialization
import {
  VerificationController,
  createVerificationController,
  autoInitialize,
} from './verification';
import { AppBouncer, createAppBouncer } from './orchestration';
import { RequirementsService } from './verification';
import { PollingService } from './verification';
import { UIStateMachine } from './orchestration';
import { VerificationStateManager, verificationState } from './ui/state/verification-state';

// Face detection utilities (for debug.html and advanced usage)
import {
  computeEAR,
  computeEyeEAR,
  computeMAR,
  computeSmileMetric,
  computeBrowRaise,
  estimateYaw,
  estimatePitch,
  estimateRoll,
  analyzeBrightness,
  distance2D,
  LEFT_EYE_INDICES,
  RIGHT_EYE_INDICES,
  FEATURE_INDICES,
} from './face-detection';

// Liveness configuration (for debug.html calibration)
import {
  DEFAULT_THRESHOLDS,
  ACTION_CONFIGS,
  LIGHTING_CONFIG,
  LIVENESS_ACTIONS,
  getThreshold,
  setThresholdOverride,
  clearThresholdOverrides,
  getCurrentThresholds,
} from './liveness';

// UI Modules (extracted from ui/index.html)
import { UIApp, initUIApp, parseUrlParams } from './ui/ui-app';
import { LightingController, lightingController } from './ui/controllers/lighting-controller';
import {
  FaceDetectionController,
  faceDetectionController,
} from './ui/controllers/face-detection-controller';
import { LivenessController, livenessController } from './ui/controllers/liveness-controller';
import { DocumentController, documentController } from './ui/controllers/document-controller';
import { BackendSyncService, createBackendSyncService } from './ui/services/backend-sync-service';
import * as domUtils from './ui/utils/dom-utils';
import * as imageUtils from './ui/utils/image-utils';
import { EventManager, getEventManager, initEventManager, getActionData } from './ui/events';

// Internationalization (i18n) - for translations
import { i18n, t } from './i18n';

// ============================================================
// Build-time Configuration (injected by esbuild)
// ============================================================

/**
 * API Base URL - injected at build time via esbuild define
 * Set API_BASE_URL env var before building
 */
declare const API_BASE_URL: string;

/**
 * Assets Base URL - injected at build time via esbuild define
 * Set ASSETS_BASE_URL env var before building
 */
declare const ASSETS_BASE_URL: string;

// ============================================================
// Browser Global API
// ============================================================

/**
 * Global AppBouncer namespace for browser usage
 */
declare global {
  interface Window {
    AppBouncer: typeof AppBouncerGlobal;
    APPBOUNCER_API_URL: string;
    APPBOUNCER_ASSETS_URL: string;
  }
}

const AppBouncerGlobal = {
  // Main SDK class
  SDK: AppBouncer,
  createSDK: createAppBouncer,

  // Verification Controller (DOM bridge)
  Controller: VerificationController,
  createController: createVerificationController,

  // Services
  RequirementsService,
  PollingService,
  UIStateMachine,

  // State Management
  VerificationStateManager,
  verificationState, // Singleton instance for global state

  // ============================================================
  // Internationalization (i18n)
  // ============================================================
  i18n, // Singleton instance for translations
  t, // Shorthand translation function

  // ============================================================
  // UI Modules (extracted from ui/index.html)
  // ============================================================

  // UI App coordinator
  UIApp,
  initUIApp,
  parseUrlParams,

  // Controllers
  LightingController,
  lightingController,
  FaceDetectionController,
  faceDetectionController,
  LivenessController,
  livenessController,
  DocumentController,
  documentController,

  // Services
  BackendSyncService,
  createBackendSyncService,

  // Utilities
  domUtils,
  imageUtils,

  // Event Management
  EventManager,
  getEventManager,
  initEventManager,
  getActionData,

  // ============================================================
  // Face Detection Utilities (for debug.html and advanced usage)
  // ============================================================
  faceUtils: {
    computeEAR,
    computeEyeEAR,
    computeMAR,
    computeSmileMetric,
    computeBrowRaise,
    estimateYaw,
    estimatePitch,
    estimateRoll,
    analyzeBrightness,
    distance2D,
    LEFT_EYE_INDICES,
    RIGHT_EYE_INDICES,
    FEATURE_INDICES,
  },

  // ============================================================
  // Liveness Configuration (for debug.html calibration)
  // ============================================================
  livenessConfig: {
    DEFAULT_THRESHOLDS,
    ACTION_CONFIGS,
    LIGHTING_CONFIG,
    LIVENESS_ACTIONS,
    getThreshold,
    setThresholdOverride,
    clearThresholdOverrides,
    getCurrentThresholds,
  },

  // Version info
  version: '1.0.0',

  /**
   * Quick start - initialize with minimal configuration
   */
  async init(
    options: {
      apiKey?: string;
      apiUrl?: string;
      assetsUrl?: string;
      callbackUrl?: string;
      debug?: boolean;
    } = {}
  ): Promise<VerificationController> {
    // Get elements
    const videoElement = document.getElementById('camera-feed') as HTMLVideoElement;
    const overlayCanvas = document.getElementById('camera-overlay') as HTMLCanvasElement;
    const screenContainer = document.getElementById('screen-container') as HTMLElement;
    const statusElement = document.getElementById('status-message') as HTMLElement;
    const progressElement = document.getElementById('progress-indicator') as HTMLElement;

    if (!videoElement || !screenContainer) {
      throw new Error('Missing required elements: #camera-feed and #screen-container');
    }

    // Get API key from options or URL params
    const params = new URLSearchParams(window.location.search);
    const apiKey = options.apiKey || params.get('api_key');

    if (!apiKey) {
      throw new Error('API key is required (pass via options or ?api_key= URL param)');
    }

    // API URL - use build-time injected value (cannot be overridden by URL params for security)
    const apiUrl = options.apiUrl || API_BASE_URL;

    // Assets URL - use build-time injected value for static assets like models
    const assetsUrl = options.assetsUrl || ASSETS_BASE_URL || undefined;

    // Create and initialize controller
    const controller = await createVerificationController({
      videoElement,
      overlayCanvas,
      screenContainer,
      statusElement,
      progressElement,
      apiBaseUrl: apiUrl,
      assetsBaseUrl: assetsUrl,
      debug: options.debug ?? true,
    });

    // Store globally for debugging
    window.__appbouncer_controller = controller;

    return controller;
  },

  /**
   * Helper to get URL parameters
   */
  getUrlParams(): { apiKey: string | null; callbackUrl: string | null } {
    const params = new URLSearchParams(window.location.search);
    return {
      apiKey: params.get('api_key'),
      callbackUrl: params.get('callback_url'),
    };
  },

  /**
   * Get the configured API URL (build-time injected)
   */
  getApiUrl(): string {
    return API_BASE_URL;
  },

  /**
   * Get the configured Assets URL (build-time injected)
   * Returns empty string if not configured (uses relative paths)
   */
  getAssetsUrl(): string {
    return ASSETS_BASE_URL;
  },

  /**
   * Build a full asset URL from a relative path
   * @param relativePath - Relative path like 'models/age_fp16.onnx'
   * @returns Full URL or relative path if ASSETS_BASE_URL is not set
   */
  buildAssetUrl(relativePath: string): string {
    const baseUrl = ASSETS_BASE_URL;
    if (!baseUrl) {
      // No assets base URL configured, use relative path
      return relativePath.startsWith('./') ? relativePath : `./${relativePath}`;
    }
    // Build absolute URL
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const cleanPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    return `${cleanBase}${cleanPath}`;
  },
};

// Make available globally
if (typeof window !== 'undefined') {
  window.AppBouncer = AppBouncerGlobal;

  // Export API URL for HTML to use (build-time injected, not user-configurable)
  window.APPBOUNCER_API_URL = API_BASE_URL;

  // Export Assets URL for HTML to use (build-time injected)
  window.APPBOUNCER_ASSETS_URL = ASSETS_BASE_URL;

  // Auto-detect browser language and initialize i18n
  i18n.detectBrowserLanguage();

  // Auto-initialize if enabled via data attribute on script tag
  const currentScript = document.currentScript as HTMLScriptElement;
  if (currentScript?.dataset.autoInit !== 'false') {
    autoInitialize();
  }
}

export default AppBouncerGlobal;
